// Blocks the iOS Safari edge-swipe back/forward navigation gesture so a kiosk
// customer can't swipe away from the current page and out of the flow.
//
// `overscroll-behavior: none` (index.css) suppresses rubber-banding and
// pull-to-refresh but NOT the horizontal history swipe. On iOS 13.4+ that
// gesture is only reliably cancelled by calling preventDefault() on a
// `touchstart` that begins inside the edge zone — by the first `touchmove`
// Safari has already committed to the navigation.
//
// To avoid disturbing anything else we scope it as tightly as possible:
//   - only single-finger touches (leaves pinch-zoom / multi-touch alone)
//   - only touches that start within EDGE_THRESHOLD_PX of the left/right edge
//     (taps and scrolls anywhere else are never touched)
//
// https://pqina.nl/blog/blocking-navigation-gestures-on-ios-13-4/

const EDGE_THRESHOLD_PX = 20;

/**
 * Start blocking the edge-swipe navigation gesture. Returns a cleanup function
 * that removes the listener.
 */
export function disableSwipeNavigation(): () => void {
  const handleTouchStart = (event: TouchEvent) => {
    // Never interfere with multi-touch gestures (e.g. debug pinch-zoom).
    if (event.touches.length !== 1) return;

    const { pageX } = event.touches[0];
    const isNearEdge =
      pageX <= EDGE_THRESHOLD_PX ||
      pageX >= window.innerWidth - EDGE_THRESHOLD_PX;

    // Only edge touches can trigger the history swipe; leave the rest alone.
    if (!isNearEdge) return;

    event.preventDefault();
  };

  // Must be non-passive so preventDefault() is honoured.
  document.addEventListener("touchstart", handleTouchStart, { passive: false });

  return () => {
    document.removeEventListener("touchstart", handleTouchStart);
  };
}
